import { Fragment, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { getUrl } from 'aws-amplify/storage'
import FiltersModal, { FilterType } from '../../common/FiltersModal'
import SearchWithFilter from '../../common/SearchWithFilter'
import EmptyPageResults from '../../common/EmptyPageResults'
import LoadingSpinner from '../../common/LoadingSpinner'
import SimpleButton from '../../common/SimpleButton'
import ConfirmationModal from '../../common/ConfirmationModal'
import { toast } from '../../common/Toast'
import { capitalizeWords } from '../../../utils/functions'
import { DownloadIcon, TrashCanFull } from '../../../icons'
import { no_image_200x200 } from '../../../images'
import { productModelsDeletePermissions, productModelsWritePermissions } from '../../../utils/permissionValidation'
import { clearProductModelsMessages, loadProductModelsImages, removeProductModelsImages } from '../../../actions/productModels'
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect'
import ViewModelImageModal from './ViewModelImageModal'
import AddModelImageModal from './AddModelImageModal'
import '../uploads.scss'

const formatBytes = (bytes) => {
  const n = Number(bytes || 0)
  if (!n) return '-'
  const k = 1024
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.min(Math.floor(Math.log(n) / Math.log(k)), units.length - 1)
  const v = n / Math.pow(k, i)
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`
}

const ImageTile = ({ item, t, isSelected, onToggleSelect, handleDownloadSingle, onDelete, canDelete, onOpen }) => {
  const fileName = item?.fileName || '-'
  const imageTitle = item?.imageTitle || '-'
  const dims = item?.width && item?.height ? `${item.width} x ${item.height}` : '-'
  const size = formatBytes(item?.fileSize)

  return (
    <div
      className={`image-card${isSelected ? ' is-selected' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen?.(item)
      }}
    >
      <div className="preview">
        <img src={item?.url || no_image_200x200} alt={fileName} className="img" />

        <div className="hover-actions">
          <label
            className="check"
            onClick={(e) => {
              e.stopPropagation()
              onToggleSelect(item.id)
            }}
          >
            <input type="checkbox" checked={!!isSelected} onChange={() => {}} />
          </label>
          <div className="download">
            <DownloadIcon
              onClick={(e) => {
                e.stopPropagation()
                handleDownloadSingle(item)
              }}
            />
          </div>
        </div>
      </div>
      <div className="meta">
        <div className="name" title={imageTitle}>
          {capitalizeWords(String(imageTitle))}
        </div>
        <div className="bottom">
          <div className="sub">
            <span>{size}</span>
            {dims !== '-' && (
              <>
                <span className="dot">•</span>
                <span>{dims}</span>
              </>
            )}
          </div>
          {canDelete && (
            <div
              className="trash"
              onClick={(e) => {
                e.stopPropagation()
                onDelete(item)
              }}
              title={t('delete')}
            >
              <TrashCanFull />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const ModelImages = (props) => {
  const { t } = useTranslation()
  const [selectedFilterModel, setSelectedFilterModel] = useState(null)
  const [selectedFilterProductType, setSelectedFilterProductType] = useState(null)
  const [searchParam, setSearchParam] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [showViewImageModal, setShowViewImageModal] = useState(false)
  const [showAddImageModal, setShowAddImageModal] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const [imageToDelete, setImageToDelete] = useState(null)
  const productTypeLabels = props.productTypes?.map((pt) => pt.productType || []) || []
  const modelLabels = props.models?.map((m) => m.model || []) || []
  const images = props.productModelsImages || []
  const hasData = images.length > 0

  useEffect(() => setCanLoadMore(props.canLoadMore), [props.canLoadMore])

  useEffect(() => setShowSpinner(props.isLoading), [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)
      props.actions.clearProductModelsMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)
      props.actions.clearProductModelsMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) handleLoadData(false)
  }, [selectedPage])

  useDeepCompareEffect(() => {
    const timer = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadData(true)
        }
        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => clearTimeout(timer)
  }, [searchParam, selectedFilterModel, selectedFilterProductType])

  const handleLoadData = (withReset) => {
    let queryParams = createQueryParams()
    queryParams = { ...queryParams, startPage: withReset ? 1 : selectedPage }
    props.actions.loadProductModelsImages(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 1) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleLoadMore = () => setSelectedPage((p) => (p ? p + 1 : 2))

  const handleResetFilters = () => {
    setSelectedFilterModel(null)
    setSelectedFilterProductType(null)
  }

  const createQueryParams = () => {
    let queryParams = {}
    if (searchParam) queryParams = { ...queryParams, query: searchParam }
    if (selectedFilterModel) queryParams = { ...queryParams, model: selectedFilterModel }
    if (selectedFilterProductType) queryParams = { ...queryParams, productType: selectedFilterProductType }
    return queryParams
  }

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const clearSelection = () => setSelectedIds(new Set())

  const closeModal = () => {
    props.actions.clearProductModelsMessages()
    setSelectedImage(null)
    setShowViewImageModal(false)
    setShowAddImageModal(false)
  }

  const getSignedUrl = async (item) => {
    const key = String(item?.filePath || '').replace(/^\/+/, '')
    if (!key) return null
    const result = await getUrl({
      key,
      options: {
        accessLevel: 'public',
      },
    })
    return result.url.toString()
  }

  const onDownloadOne = async (img) => {
    try {
      const signed = await getSignedUrl(img)
      if (!signed) return
      const res = await fetch(signed)
      const blob = await res.blob()
      const a = document.createElement('a')
      const blobUrl = URL.createObjectURL(blob)
      a.href = blobUrl
      a.download = img.fileName || 'image'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(blobUrl)
      return true
    } catch (error) {
      return false
    }
  }

  const handleDownloadSingle = async (item) => {
    setShowSpinner(true)
    try {
      const ok = await onDownloadOne(item)
      if (ok) toast.success(t('product_models_context.image_download_success', { count: 1 }))
      else toast.error(t('error.something_wrong'))
    } finally {
      setShowSpinner(false)
    }
  }

  const onDownloadZip = async () => {
    const ids = Array.from(selectedIds).map((x) => String(x))
    if (!ids.length) return
    const selected = images.filter((x) => ids.includes(String(x.id)))
    if (!selected.length) return
    setShowSpinner(true)

    let successCount = 0

    try {
      for (const img of selected) {
        const ok = await onDownloadOne(img)
        if (ok) successCount++
        await new Promise((r) => setTimeout(r, 250))
      }

      if (successCount > 0) {
        toast.success(t('product_models_context.image_download_success', { count: successCount }))
      } else {
        toast.error(t('error.something_wrong'))
      }

      clearSelection()
    } finally {
      setShowSpinner(false)
    }
  }

  const onAskDelete = (img) => {
    setImageToDelete(img)
    setShowDeleteConfirmModal(true)
  }

  const onConfirmDelete = () => {
    setShowDeleteConfirmModal(false)
    if (!imageToDelete) return
    props.actions.removeProductModelsImages(imageToDelete?.deviceModel?.id, imageToDelete?.id)
  }

  const selectedCount = selectedIds.size
  const tiles = useMemo(() => images.map((img) => ({ ...img, id: img?.id })), [images])

  return (
    <Fragment>
      <div className="uploads-wrapper model-images-page">
        <div className="actions-wrapper flex-justify-between">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={selectedFilterModel || selectedFilterProductType}
            counterBadge={(selectedFilterModel && 1) + (selectedFilterProductType && 1)}
            minSearchLength={2}
          />
          <div className="model-images-toolbar">
            {selectedCount > 0 && (
              <>
                <SimpleButton className="action-button secondary" onClick={clearSelection} disabled={!!showSpinner}>
                  {t('clear_all')}
                </SimpleButton>
                <SimpleButton className="action-button" onClick={onDownloadZip} disabled={!!showSpinner}>
                  {t('download')} ({selectedCount})
                </SimpleButton>
              </>
            )}
            <SimpleButton className="submit-button" onClick={() => setShowAddImageModal(true)} requiredPermission={productModelsWritePermissions}>
              {t('product_models_context.add_new_image')}
            </SimpleButton>
          </div>
        </div>

        {hasData && (
          <div className="images-grid">
            {tiles.map((img) => (
              <ImageTile
                key={img.id}
                item={img}
                t={t}
                isSelected={selectedIds.has(img.id)}
                onToggleSelect={toggleSelect}
                handleDownloadSingle={handleDownloadSingle}
                onDelete={onAskDelete}
                canDelete={true}
                onOpen={(item) => {
                  setSelectedImage(item)
                  setShowViewImageModal(true)
                }}
              />
            ))}
          </div>
        )}

        {!showSpinner && !isFirstLoad && !hasData && <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />}

        {canLoadMore && !showSpinner && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={handleLoadMore}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>

      {showFiltersModal && (
        <FiltersModal
          filters={[
            { name: t('model'), type: FilterType.singleSelect, dataset: modelLabels, input: selectedFilterModel, output: (m) => setSelectedFilterModel(m) },
            {
              name: t('product_type'),
              type: FilterType.singleSelect,
              dataset: productTypeLabels,
              input: selectedFilterProductType,
              output: (pt) => setSelectedFilterProductType(pt),
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={handleResetFilters}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}

      {showSpinner && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}

      {showViewImageModal && selectedImage && (
        <ViewModelImageModal imageDetails={selectedImage} closeModal={closeModal} onDownload={() => handleDownloadSingle(selectedImage)} />
      )}

      {showAddImageModal && (
        <AddModelImageModal closeModal={closeModal} onSuccess={() => handleLoadData(true)} setShowSpinner={setShowSpinner} showSpinner={showSpinner} />
      )}

      {showDeleteConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteConfirmModal(false)}
          onAccept={onConfirmDelete}
          onCancel={() => setShowDeleteConfirmModal(false)}
          message={t('product_models_context.delete_image_confirmation')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={productModelsDeletePermissions}
        />
      )}
    </Fragment>
  )
}

function stateToProps({ productModels }) {
  return {
    productModelsImages: productModels?.productModelsImages || [],
    models: productModels?.productModelsFilterOptions?.models || [],
    productTypes: productModels?.productModelsFilterOptions?.productTypes || [],
    isLoading: productModels?.isLoading,
    canLoadMore: productModels?.canLoadMore,
    successMessage: productModels?.successMessage,
    errorMessage: productModels?.errorMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadProductModelsImages,
        removeProductModelsImages,
        clearProductModelsMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(ModelImages)
